/**
 * フィールドラベルマッピング
 * APIフィールド名を日本語ラベルに変換
 */

export const FIELD_LABELS: Record<string, string> = {
  // 予約 - 基本情報
  id: 'ID',
  contact_code: '予約番号',
  start_date: '貸出日時',
  end_date: '返却日時',
  startDate: '貸出日時',
  endDate: '返却日時',
  status: 'ステータス',
  status_name: 'ステータス名',
  status_code: 'ステータスコード',
  booking_date: '予約日',
  bg_color: '背景色',
  text_color: '文字色',

  // 予約 - 顧客情報
  customer_name: '顧客名',
  customerName: '顧客名',
  customer_email: 'メールアドレス',
  customer_tel: '電話番号',
  customer_address: '住所',
  past_reservation_count: '過去予約回数',

  // 予約 - 車両情報
  vehicle_id: '車両',
  vehicleId: '車両',
  resource_id: 'リソース',
  resourceId: 'リソース',
  car_type: '車種',
  carType: '車種',

  // 予約詳細 (detail.*)
  'detail.contact_id': '連絡先ID',
  'detail.contact_media': '予約経路',
  'detail.contact_plan': 'プラン',
  'detail.communication_tools': '連絡手段',
  'detail.communication_tools_display': '連絡手段（表示）',
  'detail.pax': '乗客数（大人）',
  'detail.pax_children': '乗客数（子供）',
  'detail.child_seat': 'チャイルドシート',
  'detail.booster_seat': 'ブースターシート',
  'detail.junior_seat': 'ジュニアシート',
  'detail.arrival_flight': '到着便',
  'detail.departure_flight': '出発便',
  'detail.base_price': '基本料金',
  'detail.option': 'オプション',
  'detail.option_price': 'オプション料金',
  'detail.noc_price': 'NOC料金',
  'detail.cdw_price': 'CDW料金',
  'detail.sum_price': '合計金額',
  'detail.invoice_link': '請求書リンク',
  'detail.comment': 'コメント',
  'detail.memo': 'メモ',
  'detail.license': '免許証確認',
  'detail.payment': '支払い確認',
  'detail.terms_and_conditions': '利用規約同意',
  'detail.disclaimer': '免責事項同意',
  'detail.agreement': '契約同意',

  // 車両
  vehicle_code: '車両コード',
  vehicleCode: '車両コード',
  vehicle_type: '車種タイプ',
  vehicleType: '車種タイプ',
  completion: '整備状況',
  active: '稼働状態',
  active_from: '稼働開始日',
  active_to: '稼働終了日',
  cleaning: '清掃',
  car_wash: '洗車',
  check1: 'チェック1',
  check2: 'チェック2',
  tire_groove: 'タイヤ溝',
  tire_pressure: 'タイヤ空気圧',

  // 車種タイプ
  vehicle_type_code: '車種コード',
  vehicle_type_name: '車種名',
  vehicle_type_category: 'カテゴリ',
  vehicle_type_capacity: '定員',
  vehicle_type_description: '説明',
  vehicle_type_active: '有効',
  vehicle_type_priority: '優先度',

  // リソース
  resource_name: 'リソース名',
  resource_order: '表示順',

  // タイムスタンプ
  created_at: '作成日時',
  updated_at: '更新日時',
  createdAt: '作成日時',
  updatedAt: '更新日時',

  // スタッフ関連
  pickup_staff_id: 'PUスタッフ',
  dropoff_staff_id: 'DOスタッフ',
  rental_staff_id: '貸出スタッフ',
  return_staff_id: '返却スタッフ',
  pu_staff_id: 'PUスタッフ',
  do_staff_id: 'DOスタッフ',
  rent_staff_id: '貸出スタッフ',

  // 貸出・返却場所
  rental_location: '貸出場所',
  return_location: '返却場所',

  // メールフラグ
  mail_send_flg1: 'メール送信1',
  mail_send_flg2: 'メール送信2',
  mail_send_flg3: 'メール送信3',
};

interface Change {
  field: string;
  label?: string;
  [key: string]: unknown;
}

/**
 * フィールド名から日本語ラベルを取得
 * @param field フィールド名
 * @returns 日本語ラベル（見つからない場合は元のフィールド名）
 */
export function getFieldLabel(field: string): string {
  if (!field) return '';
  return FIELD_LABELS[field] || field;
}

/**
 * ネストされたフィールド名から日本語ラベルを取得
 * @param prefix プレフィックス（例: 'detail'）
 * @param field フィールド名
 * @returns 日本語ラベル
 */
export function getNestedFieldLabel(prefix: string, field: string): string {
  if (!field) return '';
  const key = prefix ? `${prefix}.${field}` : field;
  return FIELD_LABELS[key] || getFieldLabel(field);
}

/**
 * 変更内容にラベルを付与
 * @param changes 変更内容の配列
 * @returns ラベル付きの変更内容
 */
export function addLabelsToChanges(changes: Change[]): (Change & { label: string })[] {
  if (!Array.isArray(changes)) return [];

  return changes.map(change => ({
    ...change,
    label: change.label || getFieldLabel(change.field),
  }));
}

/**
 * エンティティタイプの日本語ラベル
 */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  reservation: '予約',
  vehicle: '車両',
  vehicleType: '車種タイプ',
  resource: 'リソース',
  payment: '支払い',
};

/**
 * 変更タイプの日本語ラベル
 */
export const CHANGE_TYPE_LABELS: Record<string, string> = {
  create: '新規作成',
  update: '更新',
  delete: '削除',
};

/**
 * 変更ソースの設定
 */
export const SOURCE_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  user: {
    label: 'ユーザー',
    color: 'primary',
    icon: '👤',
  },
  sync: {
    label: '同期',
    color: 'secondary',
    icon: '🔄',
  },
  pusher: {
    label: 'Pusher',
    color: 'success',
    icon: '🔔',
  },
};

/**
 * 値を表示用にフォーマット
 * @param field フィールド名
 * @param value 値
 * @returns フォーマットされた値
 */
// ステータス値のマッピング
const STATUS_MAP: Record<string | number, string> = {
  1: '予約確定',
  2: '貸出中',
  3: '返却済',
  4: 'キャンセル',
  5: '仮予約',
  6: '整備中',
  7: '清掃中',
  8: '予約不可',
  9: 'その他',
  'booked': '予約確定',
  'in_use': '貸出中',
  'returned': '返却済',
  'cancelled': 'キャンセル',
  'pending': '仮予約',
  'maintenance': '整備中',
  'cleaning': '清掃中',
  'unavailable': '予約不可',
  'other': 'その他',
};

export function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return '（なし）';
  if (value === '') return '（なし）';

  // ステータスフィールド
  if (field === 'status') {
    return STATUS_MAP[value as string | number] || String(value);
  }

  // 日付フィールド
  if (field.includes('date') || field.includes('Date') || field === 'created_at' || field === 'updated_at') {
    if (typeof value === 'string') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
        }
      } catch {
        return String(value);
      }
    }
  }

  // ブール値
  if (typeof value === 'boolean') {
    return value ? 'はい' : 'いいえ';
  }

  // 数値（金額フィールド）
  if (field.includes('price') || field.includes('Price')) {
    if (typeof value === 'number') {
      return `¥${value.toLocaleString()}`;
    }
  }

  // 配列
  if (Array.isArray(value)) {
    if (value.length === 0) return '（なし）';
    // 配列内のオブジェクトを適切にフォーマット
    return value.map((item: unknown) => {
      if (item === null || item === undefined) return '';
      if (typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        // id, name などのプロパティがあれば優先的に表示
        if (obj.name) return obj.name;
        if (obj.id) return String(obj.id);
        try {
          return JSON.stringify(item);
        } catch {
          return '[オブジェクト]';
        }
      }
      return String(item);
    }).filter(Boolean).join(', ') || '（なし）';
  }

  // オブジェクト
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // id, name などのプロパティがあれば優先的に表示
    if (obj.name) return obj.name as string;
    if (obj.id) return String(obj.id);
    try {
      return JSON.stringify(value);
    } catch {
      return '[オブジェクト]';
    }
  }

  return String(value);
}
